import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus, Wizard} from 'platform/components';

import {useEffect, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {mergeDeepLeft} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetVehicleQuery,
  usePutRentalVehicleMutation,
  useUpdateVehicleMutation,
} from '@dms/api/vehicle';
import {
  RentalVehicle,
  RentalVehicleValidationSchema,
  toAPIVehicle,
  toBaseVehicle,
  useAdditionalNote,
  useGetCarFeatures,
  useVehicleFormContext,
  useWizardTabRouting,
  useFormKeyboardNavigation,
  useVehicleDocumentSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {formatDocumentTitle, FullScreenModal, handleApiError} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {RentalBasicInformation} from '../(sections)/RentalBasicInformation';
import {RentalFeatures} from '../(sections)/RentalFeatures';
import {RentalInformation} from '../(sections)/RentalInformation';
import {RentalVehicleData} from '../(sections)/RentalVehicleData';
import {ServiceIntervals} from '../(sections)/ServiceIntervals';
import {CREATE_RENTAL_VEHICLE_DEFAULT_VALUES} from '../constants/CREATE_RENTAL_VEHICLE_DEFAULT_VALUES';
import {useRentalVehicleWizard} from '../hooks/useRentalVehicleWizard';

interface CreateRentalForVehicleFormProps {
  isCreating: boolean;
  onSave: (values: RentalVehicle) => void;
  onDiscard: () => void;
  onVehicleTypeChange?: (type: string) => void;
}

const CreateRentalForVehicleFrom = ({
  onSave,
  onDiscard,
  isCreating,
  onVehicleTypeChange,
}: CreateRentalForVehicleFormProps) => {
  const {control, formApi} = useVehicleFormContext<RentalVehicle>();

  const wizardSteps = useRentalVehicleWizard(control, {
    basicInformation: (
      <RentalBasicInformation
        disableVinDecoder
        infoAlert={{
          title: i18n.t('entity.vehicle.labels.creatingRentalForSaleVehicleMessage'),
          message: i18n.t('entity.vehicle.labels.creatingRentalForSaleVehicle'),
        }}
      />
    ),
    vehicleData: <RentalVehicleData />,
    features: <RentalFeatures />,
    serviceIntervals: <ServiceIntervals />,
    rentalInformation: <RentalInformation />,
  });

  const tabStepMap = useMemo(
    () => ({
      'basic-information': 0,
      'vehicle-data': 1,
      features: 2,
      'service-intervals': 3,
      'rental-information': 4,
    }),
    []
  );

  const {wizardRef, onStepChange} = useWizardTabRouting({
    tabStepMap,
    tabRoutePattern: vehiclesRoutes.createRentalVehicleFromExistingVehicleV2,
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  const vehicleTypeWatch = useWatch({
    control,
    name: 'type',
  });

  useEffect(
    function notifyVehicleTypeChange() {
      onVehicleTypeChange?.(vehicleTypeWatch?.[0] as string);
    },
    [vehicleTypeWatch] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return (
    <FullScreenModal
      data-testid="createRentalForVehicle"
      headline={i18n.t('entity.vehicle.labels.createRentalVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isCreating,
          onClick: onDiscard,
        },
        {
          title: i18n.t('general.actions.create'),
          isLoading: isCreating,
          onClick: () => {
            formApi.handleSubmit(onSave, (error) => {
              if (process.env.ENV_TYPE === 'dev') {
                console.error(error);
              }
            })();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};

export function CreateRentalForVehicle() {
  const navigate = useNavigate();

  const {id: vehicleId} = useRequiredParams();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const [getCarFeatures] = useGetCarFeatures();
  const [createRentalVehicle, {isLoading: isCreatingRental}] = usePutRentalVehicleMutation();
  const [updateVehicleMutation, {isLoading: isUpdatingVehicle}] = useUpdateVehicleMutation();

  const isCreating = isUpdatingVehicle || isCreatingRental;

  const {handleSaveNote} = useAdditionalNote();
  const {syncDocuments} = useVehicleDocumentSync();

  const handleSave = async (values: RentalVehicle) => {
    const {rentalProperties, ...vehicleSubmitValues} = values;

    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(vehicleSubmitValues, null, sourcingData);

    await updateVehicleMutation({
      vehicleId,
      updateVehicleRequestBody: apiPayload,
    })
      .unwrap()
      .then(() => {
        handleSaveNote(vehicleId, vehicleSubmitValues.additionalNote ?? '');
      })
      .catch(handleApiError);

    await createRentalVehicle({
      vehicleId,
      rentalVehicleRequestBody: rentalProperties,
    })
      .unwrap()
      .catch(handleApiError);

    await syncDocuments({
      vehicleId,
      contextTarget: 'vehicle',
      fileFields: [
        {
          fieldValue: vehicleSubmitValues.batteryCertificateDocument,
          documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
        },
      ],
    });

    navigate(composePath(vehiclesRoutes.detail, {params: {id: vehicleId}}));
  };

  const baseVehicle = vehicle ? toBaseVehicle(vehicle) : undefined;
  const defaultValues = vehicle
    ? {
        ...baseVehicle,
        serviceIntervals: mergeDeepLeft(
          baseVehicle?.serviceIntervals ?? {},
          CREATE_RENTAL_VEHICLE_DEFAULT_VALUES.serviceIntervals ?? {}
        ),
      }
    : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.createRentalVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isVehicleLoading} isError={isVehicleError}>
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={RentalVehicleValidationSchema}
          defaultValues={defaultValues}
        >
          <CreateRentalForVehicleFrom
            onSave={handleSave}
            onDiscard={() => navigate(-1)}
            isCreating={isCreating}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
